import { extendObservable, reaction, action, computed } from 'mobx';
import { Exclude, Expose, classToPlain } from 'class-transformer';
import { simplyDeserialize } from '../../common_components/serialization/common';
import { safeMomentDate } from './functions';
import moment from 'moment';

@Exclude()
export default class FeedbackChain {
  constructor(store, id, persisted = true) {
    this.store = store;
    extendObservable(this, {
      @Expose()
      id: id,

      @Expose()
      category: '',

      @Expose()
      title: '',

      @Expose()
      cardId: null,

      @Expose()
      description: '',

      createdAt: null,

      updatedAt: null,

      doneAt: null,

      edited: false,

      @Expose()
      controllerName: '',

      @Expose()
      actionName: '',

      @Expose()
      priority: '',

      @Expose()
      startupId: 0,

      @Expose()
      state: '',

      comments: [],

      @Expose()
      feedbackParticipants: [],

      @Expose()
      attachments: [],

      @Expose()
      valuePropositionId: undefined,

      persisted: persisted,

      creator: {},

      attachmentFilesQueue: [],

      isEditingTitle: false,
      isEditingDescription: false,

      cardLink: undefined,

      updating: false,

      rating: undefined,

      feedbackChainsStatistic: {},
    });

    this.autoUpdate = true;

    // Automatically update in backend when there is any change in
    // the tracked data
    this.updateDisposer = reaction(
      () => {
        let trackedData = {
          category: this.category,
          title: this.title,
          description: this.description,
          priority: this.priority,
          rating: this.rating,
          state: this.state,
          isEditing: this.isEditing,
          cardId: this.cardId,
          valuePropositionId: this.valuePropositionId,
        };
        return trackedData;
      },
      () => {
        if (this.persisted && this.autoUpdate && !this.isEditing) {
          if (this.store) {
            this.store.updateBackend(this);
          }
        }
      },
    );
  }

  @computed
  get isEditing() {
    return this.isEditingTitle || this.isEditingDescription;
  }

  serialize() {
    let basicSerialize = classToPlain(this);
    basicSerialize['createdAt'] = this.createdAt;
    basicSerialize['updatedAt'] = this.updatedAt;
    basicSerialize['feedbackChainsStatisticAttributes'] = {
      id: this.feedbackChainsStatistic.id,
      satisfactionRating: this.rating,
    };
    return basicSerialize;
  }

  toJson() {
    return this.serialize();
  }

  static deserialize(json, store) {
    let result = simplyDeserialize(FeedbackChain, json);
    result['createdAt'] = safeMomentDate(json.createdAt);
    result['updatedAt'] = safeMomentDate(json.updatedAt);
    result['doneAt'] = safeMomentDate(json.doneAt);
    result.store = store;
    if (store) {
      result.feedbackParticipants = store.feedbackParticipantsStore.loadFromJsonArray(
        json.feedbackParticipants,
        result,
      );
      result.comments = store.feedbackCommentsStore.loadFromJsonArray(
        json.comments,
        result,
      );
      result.attachments = store.attachmentsStore.attachmentsFromJsonArray(
        json.attachments,
        result,
      );
    }
    return result;
  }

  updateFromJson(json) {
    const tempAutoupdate = this.autoUpdate;
    this.autoUpdate = false;
    FeedbackChain.allowedInstanceProperties.forEach(prop => {
      if (json[prop]) {
        if (['createdAt', 'updatedAt', 'doneAt'].includes(prop)) {
          this[prop] = safeMomentDate(json[prop]);
        } else {
          this[prop] = json[prop];
        }
      }
    });
    this.autoUpdate = tempAutoupdate;
  }

  // List of users currently assigned to the feedback.
  // The list is returned from the startupTeam array.
  get assignees() {
    const participantIds = this.feedbackParticipants
      .filter(p => p.assignee == true)
      .map(p => p.participant.id);
    const team = this.store.rootStore.startupTeam;
    return team
      .filter(member => participantIds.includes(member.user.id))
      .map(member => member.user);
  }

  // List of users to be assigned to the feedback.
  // The list is returned from the startupTeam array.
  get assignables() {
    const team = this.store.rootStore.startupTeam;
    return team.map(teamMember => teamMember.user);
  }

  updateAssignees(assignees) {
    const team = this.store.rootStore.startupTeam;
    team.forEach(member => {
      const user = member.user;
      const participation = this.getParticipationByUser(user);
      const isAssigned = assignees.includes(user);
      if (participation) {
        participation.assignee = isAssigned;
      } else if (isAssigned) {
        const newParticipation = { assignee: true };
        this.store.feedbackParticipantsStore.createFeedbackParticipant(
          this,
          user,
          newParticipation,
        );
      }
    });
  }

  updateNotifications(type, val) {
    const currentUser = this.store.rootStore.user;
    const participation = this.getParticipationByUser(currentUser);
    if (participation) {
      participation[type] = val;
    } else if (val) {
      const newParticipation = { [type]: val };
      this.store.feedbackParticipantsStore.createFeedbackParticipant(
        this,
        currentUser,
        newParticipation,
      );
    }
  }

  getParticipationByUser(user) {
    const participation = this.feedbackParticipants.find(
      participation => participation.participant.id == user.id,
    );
    return participation;
  }

  isParticipant(user) {
    const participation = this.feedbackParticipants.find(
      f => f.participant.id == user.id,
    );
    return participation;
  }

  isAssignee(user) {
    return this.assignees.find(a => a.id == user.id) != undefined;
  }

  destroy() {
    this.store.destroy(this);
  }

  dispose() {
    this.updateDisposer();
  }

  isUnassigned() {
    return this.assignees.length == 0;
  }

  isDone() {
    return this.state == FeedbackChain.states.DONE;
  }

  isNotDone() {
    return !this.isDone() && !this.isPending();
  }

  isNotClosed() {
    return !this.isClosed() && !this.isPending();
  }

  isClosed() {
    return this.state == FeedbackChain.states.CLOSED;
  }

  isMine() {
    try {
      const currentUser = this.store.rootStore.user;
      return this.isAssignee(currentUser);
    } catch (err) {
      return false;
    }
  }

  meetsFilters(filtersString) {
    const filters = filtersString.split('_');
    let meets = true;
    filters.forEach(filter => {
      // Metaprogramatically call the methods indicated by the filter (right)
      // Return false for the first one that returns false
      meets = meets && this[filter].call(this);
    });
    return meets;
  }

  isRecentlyDone() {
    if (!this.doneAt) {
      return false;
    }

    const today = moment();
    const isRecent = today.diff(this.doneAt, 'months') < 1;

    return this.isDone() && isRecent;
  }

  isPending() {
    return this.state == FeedbackChain.states.PENDING;
  }

  isClosed() {
    return this.state == FeedbackChain.states.CLOSED;
  }

  @action
  setTitle(title) {
    this.title = title;
    this.setIsEditingTitle(false);
  }

  // val is boolean
  @action
  setIsEditingTitle(val) {
    this.isEditingTitle = val;
  }

  @action
  setDescription(description) {
    this.description = description;
    this.setIsEditingDescription(false);
  }

  // val is boolean
  @action
  setIsEditingDescription(val) {
    this.isEditingDescription = val;
  }

  @action
  setState(s) {
    this.state = s;
  }

  @action
  setRating(r) {
    this.rating = r;
  }

  @action
  setPriority(p) {
    this.priority = p;
  }

  @action
  addAttachment(file) {
    this.attachmentFilesQueue.push(file);
    // Save attachments only if the attachable has already been persisted in backend.
    if (this.persisted) {
      this.persistAttachments();
    }
  }

  @action
  removeFileFromQueue(file) {
    this.attachmentFilesQueue.remove(file);
  }

  @action
  persistAttachments() {
    if (this.persisted) {
      while (this.attachmentFilesQueue.length > 0) {
        const file = this.attachmentFilesQueue.pop();
        this.store.attachmentsStore.create(file, this);
      }
    } else {
      throw 'AttachmentOnInvalidObjectException';
    }
  }

  canEditAttachments() {
    if (this.store) {
      return this.store.rootStore.authorizationsStore.canEditFeedbackContents(
        this,
      );
    }
  }

  @action
  setCardId(cardId) {
    this.cardId = cardId;
  }

  @action
  setValuePropositionId(vpId) {
    this.valuePropositionId = vpId;
  }

  @action
  setCategory(category) {
    this.category = category;
  }

  // val is boolean
  @action
  setUpdating(val) {
    this.updating = val;
  }

  @computed
  get filteredComments() {
    return this.comments.reverse().filter(comment => {
      if (comment.internal) {
        if (this.store.rootStore.currentUserBelongsToStartup()) {
          return true;
        }
        return false;
      }
      return true;
    });
  }

  @computed
  get lastComment() {
    return this.filteredComments.sort((a, b) => {
      const dateA = a.updatedAt;
      const dateB = b.updatedAt;
      if (dateA.diff(dateB) > 0) {
        return -1;
      }
      if (dateA.diff(dateB) == 0) {
        return 0;
      }
      return 1;
    })[0];
  }

  canComment() {
    return this.state != FeedbackChain.states.CLOSED;
  }
}

FeedbackChain.priorities = ['high_priority', 'medium_priority', 'low_priority'];

FeedbackChain.states = {
  TO_DO: 'to_do',
  DOING: 'doing',
  DONE: 'done',
  PENDING: 'pending',
  CLOSED: 'closed',
};

FeedbackChain.categories = {
  INTERNAL: 'internal',
  FEEDBACK: 'feedback',
  ISSUES: 'issues',
  SUPPORT: 'support',
};

// Make sure to give a valid FeedbackChain method name to
// every filter. A valid method returns a boolean value.
// If the filter requires many methods, separate them with underscores
FeedbackChain.viewFilters = {
  UNASSIGNED: 'isUnassigned_isNotDone_isNotClosed',
  MY_NOT_DONE: 'isMine_isNotDone_isNotClosed',
  ALL_NOT_DONE: 'isNotDone_isNotClosed',
  PENDING: 'isPending',
  RECENTLY_DONE: 'isRecentlyDone',
  DONE: 'isDone',
  CLOSED: 'isClosed',
};

FeedbackChain.filters = { ...FeedbackChain.viewFilters };

FeedbackChain.allowedInstanceProperties = [
  'category',
  'title',
  'description',
  'valuePropositionId',
  'rating',
  'feedbackChainsStatistic',
  'cardId',
  'cardLink',
  'creator',
  'createdAt',
  'updatedAt',
  'doneAt',
  'edited',
  'controllerName',
  'done',
  'actionName',
  'priority',
  'startupId',
  'state',
];

// Required by the attachments store
FeedbackChain.backendModelName = 'FeedbackChain';
